import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MOZZOAPP_INSTITUTIONAL_LOGO_URL } from '../../services/local/institutionalBranding';
import {
  getLocalAiAssistantSuggestions,
  getLocalAssistantScreen,
  getLocalAssistantGuidanceChoices,
} from '../../services/local/localAiAssistant';
import { createInternalAssistantReply } from '../../services/local/internalAssistantReply';
import { SETUP_HELP, SETUP_HELP_EVENT } from '../../services/local/restaurantSetup';
import '../../styles/LocalAiAssistant.css';
import {
  canOpenAssistantRoute,
  getLocalAssistantScopeKey,
} from '../../services/local/localAssistantAccess';

const buildMessageId = () =>
  `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

const LAUNCHER_POSITION_STORAGE_KEY = 'mozzoapp:local-ai-launcher-position:v1';
const DESKTOP_LAUNCHER_SIZE = 48;
const MOBILE_LAUNCHER_SIZE = 44;
const VIEWPORT_MARGIN = 8;
const DEFAULT_LAUNCHER_OFFSET = 16;
const PANEL_MARGIN = 12;
const PANEL_GAP = 10;
const DRAG_THRESHOLD = 4;

const getViewportSize = () => ({
  width: typeof window === 'undefined' ? 1280 : window.innerWidth,
  height: typeof window === 'undefined' ? 720 : window.innerHeight,
});

const getLauncherSize = (viewportWidth) =>
  viewportWidth < 768 ? MOBILE_LAUNCHER_SIZE : DESKTOP_LAUNCHER_SIZE;

const clampValue = (value, minimum, maximum) =>
  Math.min(Math.max(value, minimum), Math.max(minimum, maximum));

const clampLauncherPosition = (position, viewport, launcherSize) => ({
  x: clampValue(
    Number(position?.x) || 0,
    VIEWPORT_MARGIN,
    viewport.width - launcherSize - VIEWPORT_MARGIN
  ),
  y: clampValue(
    Number(position?.y) || 0,
    VIEWPORT_MARGIN,
    viewport.height - launcherSize - VIEWPORT_MARGIN
  ),
});

const getDefaultLauncherPosition = (viewport, launcherSize) =>
  clampLauncherPosition(
    {
      x: viewport.width - launcherSize - DEFAULT_LAUNCHER_OFFSET,
      y: viewport.height - launcherSize - DEFAULT_LAUNCHER_OFFSET,
    },
    viewport,
    launcherSize
  );

const restoreLauncherPosition = (viewport, launcherSize) => {
  if (typeof window === 'undefined') {
    return getDefaultLauncherPosition(viewport, launcherSize);
  }

  try {
    const storedPosition = JSON.parse(
      window.localStorage.getItem(LAUNCHER_POSITION_STORAGE_KEY) || 'null'
    );
    const availableWidth = Math.max(
      1,
      viewport.width - launcherSize - VIEWPORT_MARGIN * 2
    );
    const availableHeight = Math.max(
      1,
      viewport.height - launcherSize - VIEWPORT_MARGIN * 2
    );

    if (
      Number.isFinite(storedPosition?.xRatio) &&
      Number.isFinite(storedPosition?.yRatio)
    ) {
      return clampLauncherPosition(
        {
          x: VIEWPORT_MARGIN + storedPosition.xRatio * availableWidth,
          y: VIEWPORT_MARGIN + storedPosition.yRatio * availableHeight,
        },
        viewport,
        launcherSize
      );
    }
  } catch (error) {
    // Una posicion dañada no debe impedir que aparezca el asistente.
  }

  return getDefaultLauncherPosition(viewport, launcherSize);
};

const persistLauncherPosition = (position, viewport, launcherSize) => {
  if (typeof window === 'undefined') {
    return;
  }

  const availableWidth = Math.max(
    1,
    viewport.width - launcherSize - VIEWPORT_MARGIN * 2
  );
  const availableHeight = Math.max(
    1,
    viewport.height - launcherSize - VIEWPORT_MARGIN * 2
  );

  try {
    window.localStorage.setItem(
      LAUNCHER_POSITION_STORAGE_KEY,
      JSON.stringify({
        xRatio: clampValue(
          (position.x - VIEWPORT_MARGIN) / availableWidth,
          0,
          1
        ),
        yRatio: clampValue(
          (position.y - VIEWPORT_MARGIN) / availableHeight,
          0,
          1
        ),
      })
    );
  } catch (error) {
    // El movimiento sigue funcionando aunque el navegador no permita persistirlo.
  }
};

const getPanelPosition = (launcherPosition, viewport, launcherSize) => {
  const panelWidth = Math.min(390, Math.max(280, viewport.width - PANEL_MARGIN * 2));
  const panelHeight = Math.min(
    viewport.width < 768 ? 620 : 590,
    Math.max(360, viewport.height - PANEL_MARGIN * 2)
  );
  const preferredLeft =
    launcherPosition.x + launcherSize / 2 < viewport.width / 2
      ? launcherPosition.x
      : launcherPosition.x + launcherSize - panelWidth;
  const belowTop = launcherPosition.y + launcherSize + PANEL_GAP;
  const aboveTop = launcherPosition.y - PANEL_GAP - panelHeight;
  const fitsBelow = belowTop + panelHeight <= viewport.height - PANEL_MARGIN;
  const fitsAbove = aboveTop >= PANEL_MARGIN;
  let left = preferredLeft;
  let top = fitsBelow
    ? belowTop
    : fitsAbove
      ? aboveTop
      : clampValue(
          launcherPosition.y + launcherSize / 2 - panelHeight / 2,
          PANEL_MARGIN,
          viewport.height - panelHeight - PANEL_MARGIN
        );

  if (!fitsBelow && !fitsAbove) {
    const roomOnLeft = launcherPosition.x - PANEL_GAP - PANEL_MARGIN;
    const roomOnRight =
      viewport.width -
      (launcherPosition.x + launcherSize) -
      PANEL_GAP -
      PANEL_MARGIN;

    if (roomOnLeft >= panelWidth) {
      left = launcherPosition.x - PANEL_GAP - panelWidth;
    } else if (roomOnRight >= panelWidth) {
      left = launcherPosition.x + launcherSize + PANEL_GAP;
    }
  }

  return {
    left: clampValue(left, PANEL_MARGIN, viewport.width - panelWidth - PANEL_MARGIN),
    top: clampValue(top, PANEL_MARGIN, viewport.height - panelHeight - PANEL_MARGIN),
  };
};

const ScopedLocalAiAssistant = ({ pathname, companyName, branchName, access }) => {
  const navigate = useNavigate();
  const mountedRef = useRef(false);
  const pendingRef = useRef(false);
  const requestRef = useRef(null);
  const launcherRef = useRef(null);
  const inputRef = useRef(null);
  const messageListRef = useRef(null);
  const dragStateRef = useRef(null);
  const suppressLauncherClickRef = useRef(false);
  const [viewport, setViewport] = useState(getViewportSize);
  const launcherSize = getLauncherSize(viewport.width);
  const [launcherPosition, setLauncherPosition] = useState(() => {
    const initialViewport = getViewportSize();
    return restoreLauncherPosition(
      initialViewport,
      getLauncherSize(initialViewport.width)
    );
  });
  const [isDragging, setIsDragging] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [question, setQuestion] = useState('');
  const [isReplying, setIsReplying] = useState(false);
  const currentScreen = useMemo(
    () => getLocalAssistantScreen(pathname),
    [pathname]
  );
  const suggestedQuestions = useMemo(
    () => getLocalAiAssistantSuggestions(pathname, access),
    [pathname, access]
  );
  const [messages, setMessages] = useState(() => [
    {
      id: 'welcome',
      role: 'assistant',
      text: 'Puedo ayudarte a usar Mozzo, explicarte sus opciones y seguir el hilo de la conversacion con el manual. No consulto datos operativos en vivo ni modifico precios, stock o cobros.\n\nGoogle Gemini procesa tu pregunta y los ultimos mensajes para responder. No incluyas claves ni datos personales. Si la IA no responde, te avisare y usare la guia local.',
      actions: [],
    },
  ]);
  const guidanceChoices = getLocalAssistantGuidanceChoices(access);
  const panelPosition = useMemo(
    () => getPanelPosition(launcherPosition, viewport, launcherSize),
    [launcherPosition, launcherSize, viewport]
  );

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; requestRef.current?.abort(); };
  }, []);

  useEffect(() => {
    const prepareHelp = (event) => {
      const { step, branchId } = event.detail || {};
      if (pathname !== '/app/preparar' || branchId !== access?.branchId ||
          !access?.actions?.includes('manage_menu') || !access?.actions?.includes('manage_settings') ||
          !Number.isInteger(step) || !SETUP_HELP[step] || pendingRef.current) return;
      setQuestion(SETUP_HELP[step]);
      setIsOpen(true);
    };
    window.addEventListener(SETUP_HELP_EVENT, prepareHelp);
    return () => window.removeEventListener(SETUP_HELP_EVENT, prepareHelp);
  }, [pathname, access]);

  useEffect(() => {
    const handleResize = () => {
      const nextViewport = getViewportSize();
      const nextLauncherSize = getLauncherSize(nextViewport.width);

      setViewport(nextViewport);
      setLauncherPosition(
        restoreLauncherPosition(nextViewport, nextLauncherSize)
      );
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const focusTimeoutId = window.setTimeout(() => inputRef.current?.focus(), 80);
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
        launcherRef.current?.focus();
      }
    };

    window.addEventListener('keydown', handleEscape);

    return () => {
      window.clearTimeout(focusTimeoutId);
      window.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !messageListRef.current) {
      return;
    }

    messageListRef.current.scrollTop = messageListRef.current.scrollHeight;
  }, [isOpen, isReplying, messages]);

  const closeAssistant = () => {
    setIsOpen(false);
    window.setTimeout(() => launcherRef.current?.focus(), 0);
  };

  const submitQuestion = async (value = question) => {
    const cleanQuestion = String(value || '').trim();

    if (!cleanQuestion || pendingRef.current) {
      return;
    }
    pendingRef.current = true;

    const userMessage = {
      id: buildMessageId(),
      role: 'user',
      text: cleanQuestion,
      actions: [],
    };

    setMessages((currentMessages) => [...currentMessages, userMessage]);
    setQuestion('');
    setIsReplying(true);
    requestRef.current = new AbortController();

    try {
      const reply = await createInternalAssistantReply({
        question: cleanQuestion,
        pathname,
        companyName,
        branchName,
        access,
        history: messages.filter((message) => message.id !== 'welcome').slice(-10),
        signal: requestRef.current.signal,
      });
      if (!mountedRef.current) return;
      setMessages((currentMessages) => [
        ...currentMessages,
        {
          id: buildMessageId(),
          role: 'assistant',
          text: reply.answer,
          actions: reply.actions || [],
          guideVersion: reply.guideVersion,
          topicTitle: reply.topicTitle,
          followUpIds: reply.followUpIds || [],
          sourceKind: reply.sourceKind,
          sources: reply.sources || [],
          suggestedQuestions: reply.suggestedQuestions || [],
        },
      ]);
    } catch (error) {
      if (!mountedRef.current) return;
      setMessages((currentMessages) => [
        ...currentMessages,
        {
          id: buildMessageId(),
          role: 'assistant',
          text: 'No pude responder ahora. Probá nuevamente o pedime que abra una sección del panel.',
          actions: [],
        },
      ]);
    } finally {
      if (mountedRef.current) {
        pendingRef.current = false;
        setIsReplying(false);
      }
    }
  };

  const handleInputKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      submitQuestion();
    }
  };

  const handleNavigate = (route) => {
    if (!canOpenAssistantRoute(route, access)) return;
    navigate(route);
    setIsOpen(false);
  };

  const handleLauncherPointerDown = (event) => {
    if (event.button !== undefined && event.button !== 0) {
      return;
    }

    dragStateRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      lastX: event.clientX,
      lastY: event.clientY,
      originX: launcherPosition.x,
      originY: launcherPosition.y,
      moved: false,
    };
    suppressLauncherClickRef.current = false;
    try {
      event.currentTarget.setPointerCapture?.(event.pointerId);
    } catch (error) {
      // El gesto puede continuar aun cuando el navegador no permita capturarlo.
    }
  };

  const handleLauncherPointerMove = (event) => {
    const dragState = dragStateRef.current;

    if (!dragState || dragState.pointerId !== event.pointerId) {
      return;
    }

    const deltaX = event.clientX - dragState.startX;
    const deltaY = event.clientY - dragState.startY;

    dragState.lastX = event.clientX;
    dragState.lastY = event.clientY;

    if (
      !dragState.moved &&
      Math.hypot(deltaX, deltaY) < DRAG_THRESHOLD
    ) {
      return;
    }

    dragState.moved = true;
    suppressLauncherClickRef.current = true;
    setIsDragging(true);
    setIsOpen(false);
    setLauncherPosition(
      clampLauncherPosition(
        {
          x: dragState.originX + deltaX,
          y: dragState.originY + deltaY,
        },
        viewport,
        launcherSize
      )
    );
    event.preventDefault();
  };

  const finishLauncherDrag = (event) => {
    const dragState = dragStateRef.current;

    if (!dragState || dragState.pointerId !== event.pointerId) {
      return;
    }

    const pointerWasCancelled = event.type === 'pointercancel';
    const pointerX =
      !pointerWasCancelled && Number.isFinite(event.clientX)
        ? event.clientX
        : dragState.lastX;
    const pointerY =
      !pointerWasCancelled && Number.isFinite(event.clientY)
        ? event.clientY
        : dragState.lastY;
    const finalPosition = clampLauncherPosition(
      {
        x: dragState.originX + pointerX - dragState.startX,
        y: dragState.originY + pointerY - dragState.startY,
      },
      viewport,
      launcherSize
    );

    if (dragState.moved) {
      setLauncherPosition(finalPosition);
      persistLauncherPosition(finalPosition, viewport, launcherSize);
    }

    try {
      event.currentTarget.releasePointerCapture?.(event.pointerId);
    } catch (error) {
      // El navegador puede liberar la captura automaticamente al cancelar el gesto.
    }
    dragStateRef.current = null;
    setIsDragging(false);
  };

  const handleLauncherClick = () => {
    if (suppressLauncherClickRef.current) {
      suppressLauncherClickRef.current = false;
      return;
    }

    setIsOpen((currentValue) => !currentValue);
  };

  return (
    <div
      className={`local-ai-assistant${isDragging ? ' local-ai-assistant--dragging' : ''}`}
      data-testid="local-ai-assistant"
      style={{
        left: `${launcherPosition.x}px`,
        top: `${launcherPosition.y}px`,
      }}
    >
      {isOpen ? (
        <section
          id="mozzo-local-ai-panel"
          className="local-ai-assistant__panel"
          role="dialog"
          aria-label="Asistente de MozzoApp"
          aria-modal="false"
          style={{
            left: `${panelPosition.left}px`,
            top: `${panelPosition.top}px`,
          }}
        >
          <header className="local-ai-assistant__header">
            <img
              className="local-ai-assistant__header-logo"
              src={MOZZOAPP_INSTITUTIONAL_LOGO_URL}
              alt=""
            />
            <div className="local-ai-assistant__heading">
              <strong>Guía de Mozzo</strong>
              <span>Estás en {currentScreen.title}</span>
            </div>
            <button
              type="button"
              className="local-ai-assistant__icon-button"
              onClick={closeAssistant}
              aria-label="Cerrar asistente"
              title="Cerrar asistente"
            >
              ×
            </button>
          </header>

          <div
            ref={messageListRef}
            className="local-ai-assistant__messages"
            role="log"
            aria-live="polite"
          >
            {messages.map((message) => (
              <div
                key={message.id}
                className={`local-ai-assistant__message local-ai-assistant__message--${message.role}`}
              >
                <p>{message.text}</p>
                {message.guideVersion ? (
                  <small className="local-ai-assistant__source">
                    {message.sourceKind === 'manual-ai' ? 'IA con manual' : 'Guía local'} {message.guideVersion}{message.topicTitle ? ` · ${message.topicTitle}` : ''}
                  </small>
                ) : null}
                {message.sources?.length ? (
                  <small className="local-ai-assistant__source">
                    Fuentes: {message.sources.map((source) => source.title).join(' · ')}
                  </small>
                ) : null}
                {message.actions?.length ? (
                  <div className="local-ai-assistant__actions">
                    {message.actions.filter((action) => canOpenAssistantRoute(action.route, access)).map((action) => (
                      <button
                        key={`${message.id}-${action.route}`}
                        type="button"
                        onClick={() => handleNavigate(action.route)}
                      >
                        {action.label}
                      </button>
                    ))}
                  </div>
                ) : null}
                {message.id === messages[messages.length - 1]?.id && message.followUpIds?.length ? (
                  <div className="local-ai-assistant__actions" aria-label="Temas para continuar">
                    {guidanceChoices.filter(({ id }) => message.followUpIds.includes(id)).map((choice) => (
                      <button key={choice.id} type="button" disabled={isReplying}
                        onClick={() => submitQuestion(choice.question)}>
                        {choice.label}
                      </button>
                    ))}
                  </div>
                ) : null}
                {message.id === messages[messages.length - 1]?.id && message.suggestedQuestions?.length ? (
                  <div className="local-ai-assistant__actions" aria-label="Continuar la conversacion">
                    {message.suggestedQuestions.map((text) => (
                      <button key={text} type="button" disabled={isReplying} onClick={() => submitQuestion(text)}>
                        {text}
                      </button>
                    ))}
                  </div>
                ) : null}
              </div>
            ))}

            {isReplying ? (
              <div className="local-ai-assistant__message local-ai-assistant__message--assistant local-ai-assistant__thinking">
                <span />
                <span />
                <span />
                <span className="local-ai-assistant__sr-only">Pensando</span>
              </div>
            ) : null}
          </div>

          {messages.length === 1 ? (
            <div className="local-ai-assistant__suggestions" aria-label="Consultas sugeridas">
              {suggestedQuestions.map((suggestion) => (
                <button
                  key={suggestion}
                  type="button"
                  onClick={() => submitQuestion(suggestion)}
                >
                  {suggestion}
                </button>
              ))}
            </div>
          ) : null}

          <form
            className="local-ai-assistant__composer"
            onSubmit={(event) => {
              event.preventDefault();
              submitQuestion();
            }}
          >
            <textarea
              ref={inputRef}
              value={question}
              onChange={(event) => setQuestion(event.target.value)}
              onKeyDown={handleInputKeyDown}
              rows={1}
              maxLength={500}
              placeholder="Preguntale a MozzoApp"
              aria-label="Escribí tu consulta"
            />
            <button
              type="submit"
              className="local-ai-assistant__send"
              disabled={!question.trim() || isReplying}
              aria-label="Enviar consulta"
              title="Enviar consulta"
            >
              ↑
            </button>
          </form>
        </section>
      ) : null}

      <button
        ref={launcherRef}
        type="button"
        className="local-ai-assistant__launcher"
        onClick={handleLauncherClick}
        onPointerDown={handleLauncherPointerDown}
        onPointerMove={handleLauncherPointerMove}
        onPointerUp={finishLauncherDrag}
        onPointerCancel={finishLauncherDrag}
        onDragStart={(event) => event.preventDefault()}
        aria-label="Abrir asistente de MozzoApp"
        aria-expanded={isOpen}
        aria-controls="mozzo-local-ai-panel"
        title="Arrastrá para mover. Tocá para abrir."
      >
        <img
          src={MOZZOAPP_INSTITUTIONAL_LOGO_URL}
          alt=""
          draggable="false"
        />
      </button>
    </div>
  );
};

// Scope changes discard history and late replies, including permission revocations.
const LocalAiAssistant = (props) => (
  <ScopedLocalAiAssistant key={getLocalAssistantScopeKey(props.access)} {...props} />
);

export default LocalAiAssistant;
